import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Paper,
  Tab,
  Tabs,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Modal,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import LogoutIcon from '@mui/icons-material/Logout'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import AccountProfile from './AccountProfile'
import Reffralcode from './Reffralcode'
import PurchaseHistory from './PurchaseHistory'
import './UserPanel.scss'
import logoImage_1 from '../../assets/White PNG 1.png'
import logoImage_2 from '../../assets/Black PNG 1.png'
import {
  HiOutlineServerStack,
  HiPencilSquare,
  HiEye,
  HiOutlineShoppingBag,
  HiTrash,
  HiPencil
} from 'react-icons/hi2'
import TicketsList from '../Ticket/tickets_list'
import TicketDetails from '../Ticket/ticket_details'
import NewTicket from '../Ticket/new_ticket'
import clsx from 'clsx'

const API_BASE_URL = 'https://api.neontradepro.com'

// ... [ViewSettingsComponent remains unchanged] ...
function ViewSettingsComponent({
  loading,
  settings,
  options,
  planName
}: {
  loading: boolean
  settings: any
  options: any
  planName: string
}) {
  if (loading)
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    )
  if (!settings || settings.notConfigured) return <Typography>تنظیماتی برای این پلن ثبت نشده است.</Typography>
  if (!options) return <pre>{JSON.stringify(settings, null, 2)}</pre>

  const renderFieldValue = (fieldValue: any, fieldOption: any) => {
    if (Array.isArray(fieldValue)) {
      return fieldValue.join(', ')
    }
    if (typeof fieldValue === 'boolean') {
      return fieldValue ? 'فعال' : 'غیرفعال'
    }
    if (fieldOption.type === 'combine') {
      let combinedString = ''
      fieldOption.data?.forEach((item: any) => {
        if (typeof item === 'string') {
          combinedString += item
        } else if (item.id && fieldValue[item.id] !== undefined) {
          combinedString += ` ${fieldValue[item.id]} `
        }
      })
      return combinedString.trim()
    }
    return String(fieldValue)
  }

  const renderSetting = (key: string, option: any, value: any) => {
    const displayLabel = option.label || option.title || key

    if (option.type === 'object' && value) {
      return (
        <Box key={key} sx={{ mb: 2, p: 2, borderRadius: '8px', background: 'rgba(0,0,0,0.1)' }}>
          <Typography variant='subtitle1' sx={{ fontWeight: 'bold' }}>
            {displayLabel}
          </Typography>
          <Box sx={{ pl: 2, mt: 1 }}>
            {option.properties &&
              Object.keys(option.properties).map(propKey => {
                const propOption = option.properties[propKey]
                const propValue = value[propKey]
                if (propValue === undefined) return null
                return (
                  <Typography key={propKey} variant='body2'>
                    <strong>{propOption.label || propOption.title || propKey}:</strong>{' '}
                    {renderFieldValue(propValue, propOption)}
                  </Typography>
                )
              })}
          </Box>
        </Box>
      )
    }

    if (value === undefined) return null

    return (
      <Typography key={key} variant='body2' sx={{ mb: 1 }}>
        <strong>{displayLabel}:</strong> {renderFieldValue(value, option)}
      </Typography>
    )
  }

  return (
    <Box>
      <Typography variant='h6' component='h2' sx={{ mb: 2 }}>
        تنظیمات ذخیره شده برای پلن "{planName}"
      </Typography>
      {Object.keys(options).map(key => {
        const option = options[key]

        const fieldKey = Array.isArray(options)
          ? option.name || option.key || option.id || option.slug || key
          : key

        const value = settings[fieldKey]

        if (value === undefined) return null

        return renderSetting(key, option, value)
      })}
    </Box>
  )
}

function TabPanel(props: any) {
  const { children, value, index, ...other } = props
  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`user-panel-tabpanel-${index}`}
      aria-labelledby={`user-panel-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>{children}</Box>}
    </div>
  )
}
interface UserPanelProps {
  handleLogout: () => void
  theme: string
  isAdmin: boolean
}

interface PlanDetails {
  id: number
  title: string
  duration: number
  start_at: number
  end_at: number
}

interface UserAccount {
  id: number
  account_number: string
  account_server: string
  broker_website: string
  account_type: string
  is_active?: boolean
  plan: PlanDetails | null
  account_value?: number
}

interface PlanSettings {
  plan_settings: Map<string, any>
  can_update: boolean
  next_update_time: number
}

function timeLeftUntil(targetTimestampInSec: number): string {
  targetTimestampInSec *= 1000
  const now = Date.now()
  let diff = Math.max(0, targetTimestampInSec - now)

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) {
    const remainingHours = hours % 24
    return `${days} روز و ${remainingHours} ساعت`
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60
    return `${hours} ساعت و ${remainingMinutes} دقیقه`
  } else if (minutes > 0) {
    return `${minutes} دقیقه`
  } else {
    return `${seconds} ثانیه`
  }
}

function UserPanel({ handleLogout, theme, isAdmin }: UserPanelProps) {
  const location = useLocation()
  const navigate = useNavigate()

  const getTabValueFromUrl = () => {
    if (location.pathname.startsWith('/dashboard/tickets')) {
      return 4
    }
    const params = new URLSearchParams(location.search)
    return parseInt(params.get('tab') || '0', 10)
  }

  const tabValue = getTabValueFromUrl()

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const params = new URLSearchParams(location.search)

    if (newValue === 4) {
      navigate({
        pathname: '/dashboard/tickets',
        search: params.toString()
      })
    } else {
      if (newValue === 0) {
        params.delete('tab')
      } else {
        params.set('tab', newValue.toString())
      }
      navigate({
        pathname: '/dashboard',
        search: params.toString()
      })
    }
  }

  const [wallet, setWallet] = useState<number | null>(null)
  const [userAccounts, setUserAccounts] = useState<UserAccount[]>([])
  const [accountsLoading, setAccountsLoading] = useState<boolean>(true)
  const [accountsError, setAccountsError] = useState<string | null>(null)
  const [accountsWithFullDiscount, setAccountsWithFullDiscount] = useState<Set<number>>(new Set())

  // Modal States
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [modalText, setModalText] = useState<string>('')
  
  // View Settings States
  const [viewSettingsOpen, setViewSettingsOpen] = useState<boolean>(false)
  const [settingsToView, setSettingsToView] = useState<any>(null)
  const [optionsForView, setOptionsForView] = useState<any>(null)
  const [viewLoading, setViewLoading] = useState<boolean>(false)
  const [activePlanName, setActivePlanName] = useState<string>('')

  // Delete Account States
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [accountToDelete, setAccountToDelete] = useState<number | null>(null)
  const [actionLoading, setActionLoading] = useState(false)

  // Edit Account States
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<UserAccount | null>(null)
  const [editFormData, setEditFormData] = useState({
    account_server: '',
    broker_website: ''
  })

  // ---------------------------------------------------------------------------
  // بخش ویرایش شده: هدایت به صفحه پلن‌ها پس از ساخت اکانت
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    let needsNavigation = false
    let newPath = location.pathname
    let newTabValue = -1

    if (location.state?.fromInvite) {
      newTabValue = location.state.tab || 0
      needsNavigation = true
    } else if (location.state?.newAccountAdded) {
      // اگر حساب جدید اضافه شده، کاربر را مستقیماً به صفحه پلن‌ها بفرست
      navigate(`/plans?theme=${theme}`)
      return // توقف اجرای بقیه کدهای این افکت
    }

    if (needsNavigation) {
      if (newTabValue === 0) {
        params.delete('tab')
      } else if (newTabValue > 0) {
        params.set('tab', newTabValue.toString())
      }
      navigate(`${newPath}?${params.toString()}`, { replace: true, state: null })
    }

    fetchWallet()
    fetchUserAccounts()
  }, [location.state, location.search, location.pathname, navigate, theme])
  // ---------------------------------------------------------------------------

  const fetchWallet = async () => {
    const token = localStorage.getItem('token')
    if (!token) return

    try {
      const response = await fetch(`${API_BASE_URL}/user/info`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'خطا در دریافت مقدار کیف پول')
      }
      setWallet((await response.json()).wallet)
    } catch (err: any) {
      console.error('Failed to fetch wallet:', err)
    }
  }

  const fetchPlanSettings = async (trade_account_id: number): Promise<PlanSettings | null> => {
    const token = localStorage.getItem('token')
    if (!token) return null

    try {
      const response = await fetch(`${API_BASE_URL}/trade-account/${trade_account_id}/settings`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        const errorData = await response.json()
        if (
          (response.status === 404 && errorData.detail === 'plan settings not found') ||
          (response.status === 400 && errorData.detail === 'Plan settings not configured')
        ) {
          return null
        }
        throw new Error(errorData.detail || 'خطا در دریافت اطلاعات تنظیمات')
      }
      return await response.json()
    } catch (err: any) {
      console.error('Failed to fetch plan settings:', err)
      return null
    }
  }

  const fetchUserAccounts = async () => {
    setAccountsLoading(true)
    setAccountsError(null)
    const token = localStorage.getItem('token')
    if (!token) {
      setAccountsError('توکن احراز هویت یافت نشد. لطفاً ابتدا وارد شوید.')
      setAccountsLoading(false)
      return
    }
    try {
      const response = await fetch(`${API_BASE_URL}/trade-account`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'خطا در دریافت لیست حساب‌ها.')
      }
      const accounts: UserAccount[] = await response.json()
      setUserAccounts(accounts)
      // بررسی اینکه آیا برای این حساب‌ها فاکتور 100% موفق وجود دارد
      try {
        const invoicesResp = await fetch(`${API_BASE_URL}/invoice/plan`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        })
        if (invoicesResp.ok) {
          const invs = await invoicesResp.json()
          const invoicesList = Array.isArray(invs) ? invs : invs.items || []
          const discounted = new Set<number>()
          invoicesList.forEach((inv: any) => {
            const price = Number(inv?.price ?? inv?.amount ?? 0)
            const status = String(inv?.status || inv?.payment_status || inv?.state || '').toLowerCase()
            const paidFlags = [inv?.paid, inv?.is_paid, inv?.payment_state, inv?.payment_intent_status]
            const looksPaid =
              ['success', 'paid', 'succeeded', 'completed'].some(k => status.includes(k)) ||
              paidFlags.some(f => f === true || String(f).toLowerCase().includes('paid'))

            if (price === 0 && looksPaid) {
              const acctId = inv.trade_account_id || inv.account_id || inv.accountId || inv.trade_account?.id || inv.account?.id
              if (acctId) discounted.add(Number(acctId))
            }
          })
          setAccountsWithFullDiscount(discounted)
        }
      } catch (e) {
        console.warn('Could not fetch user invoices to detect full-discount accounts', e)
      }
    } catch (err: any) {
      console.error('Failed to fetch user accounts:', err)
      setAccountsError(err.message || 'خطا در بارگیری حساب‌های کاربری.')
    } finally {
      setAccountsLoading(false)
    }
  }


  const handleAddAccountClick = () => {
    navigate(`/add-account?theme=${theme}&fromUserPanel=true`)
  }

  const handleEditAccountClick = (account: UserAccount) => {
    setEditingAccount(account)
    setEditFormData({
      account_server: account.account_server || '',
      broker_website: account.broker_website || ''
    })
    setEditDialogOpen(true)
  }

  const submitEditAccount = async () => {
    if (!editingAccount) return
    setActionLoading(true)
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(`${API_BASE_URL}/trade-account/${editingAccount.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editFormData)
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'خطا در ویرایش حساب')
      }

      setModalText('حساب کاربری با موفقیت ویرایش شد.')
      setModalOpen(true)
      setEditDialogOpen(false)
      fetchUserAccounts() 
    } catch (err: any) {
      setModalText(err.message || 'خطا در انجام عملیات')
      setModalOpen(true)
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteAccountClick = (accountId: number) => {
    setAccountToDelete(accountId)
    setDeleteDialogOpen(true)
  }

  const confirmDeleteAccount = async () => {
    if (!accountToDelete) return
    setActionLoading(true)
    const token = localStorage.getItem('token')

    try {
      const response = await fetch(`${API_BASE_URL}/trade-account/${accountToDelete}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.detail || 'خطا در حذف حساب')
      }

      setModalText('حساب کاربری با موفقیت حذف شد.')
      setModalOpen(true)
      setDeleteDialogOpen(false)
      setAccountToDelete(null)
      fetchUserAccounts() 
    } catch (err: any) {
      setModalText(err.message || 'خطا در انجام عملیات')
      setModalOpen(true)
    } finally {
      setActionLoading(false)
    }
  }

  const handleConfigurePlanClick = async (account: UserAccount) => {
    if (account.plan) {
      const plan_settings = await fetchPlanSettings(account.id)

      if (plan_settings === null || plan_settings.can_update) {
        navigate(
          `/plan-settings?theme=${theme}&planId=${account.plan.id}&price=0&periodLabel=${encodeURIComponent(
            account.plan.duration ? `${account.plan.duration} روز` : 'نامشخص'
          )}&accountValue=${account.account_value || 0}&planName=${encodeURIComponent(
            account.plan.title || 'نامشخص'
          )}&planDuration=${account.plan.duration || 0}&accountId=${account.id}&accountNumber=${encodeURIComponent(
            account.account_number
          )}&accountServer=${encodeURIComponent(account.account_server)}`
        )
      } else {
        const time_left = timeLeftUntil(plan_settings.next_update_time)
        setModalText(`پس از گذشت ${time_left} می‌توانید تنظیمات حساب خود را تغییر دهید.`)
        setModalOpen(true)
      }
    } else {
      console.warn('Cannot configure plan: No active plan found for account', account)
      setAccountsError('پلن فعالی برای این حساب یافت نشد.')
    }
  }

  const handleViewSettingsClick = async (account: UserAccount) => {
    if (!account.plan) return

    setViewLoading(true)
    setViewSettingsOpen(true)
    setSettingsToView(null)
    setOptionsForView(null)
    setActivePlanName(account.plan.title)

    const savedSettingsData = await fetchPlanSettings(account.id)
    if (savedSettingsData === null) {
      setSettingsToView({ notConfigured: true })
      setViewLoading(false)
      return
    }
    setSettingsToView(savedSettingsData.plan_settings)

    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('Token not found')

      const plans_response = await fetch(`${API_BASE_URL}/plan`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const plans = await plans_response.json()
      const planData = plans.find((p: any) => p.id === account.plan?.id)

      if (planData && planData.plan_option_id) {
        const options_response = await fetch(`${API_BASE_URL}/plan/options/${planData.plan_option_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!options_response.ok) throw new Error('Could not fetch plan options')
        const optionsData = await options_response.json()
        setOptionsForView(optionsData.data || {})
      } else {
        setOptionsForView({})
      }
    } catch (e) {
      console.error('Error fetching plan options for view', e)
      setOptionsForView({})
    } finally {
      setViewLoading(false)
    }
  }

  const handleCloseViewSettings = () => {
    setViewSettingsOpen(false)
    setSettingsToView(null)
    setOptionsForView(null)
  }

  function RenderTicketTab() {
    if (location.pathname.startsWith('/dashboard/tickets/new')) {
      return <NewTicket />
    } else if (/\/dashboard\/tickets\/\d+/.test(location.pathname)) {
      const ticketId = location.pathname.split('/')[3]
      return <TicketDetails ticket_id={ticketId} />
    } else {
      return <TicketsList />
    }
  }

  return (
    <Container maxWidth='lg' sx={{ mt: 4, mb: 4 }} className='user-panel-wrapper'>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        aria-labelledby='modal-modal-title'
        aria-describedby='modal-modal-description'
      >
        <Box
          sx={{
            maxWidth: '90%',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: theme === 'dark' ? '#222' : '#fff',
            borderRadius: '10px',
            boxShadow: 24,
            p: 4,
            color: 'var(--text-color)'
          }}
        >
          <Typography id='modal-modal-title' variant='h6' component='h2'>
            اعلان
          </Typography>
          <Typography id='modal-modal-description' sx={{ mt: 2 }}>
            {modalText}
          </Typography>
        </Box>
      </Modal>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        className="themed-dialog"
      >
        <DialogTitle sx={{ fontFamily: 'Vazirmatn' }}>حذف حساب</DialogTitle>
        <DialogContent>
           <Typography sx={{ fontFamily: 'Vazirmatn' }}>
             آیا از حذف این حساب معاملاتی اطمینان دارید؟ این عملیات غیرقابل بازگشت است.
           </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ fontFamily: 'Vazirmatn', color: 'var(--text-color)' }}>
            انصراف
          </Button>
          <Button 
            onClick={confirmDeleteAccount} 
            color="error" 
            variant="contained" 
            disabled={actionLoading}
            sx={{ fontFamily: 'Vazirmatn' }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'حذف'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        className="themed-dialog"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontFamily: 'Vazirmatn', color: 'var(--text-color)' }}>ویرایش اطلاعات حساب</DialogTitle>
        <DialogContent>
           <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <TextField
                label="سرور حساب"
                fullWidth
                value={editFormData.account_server}
                onChange={(e) => setEditFormData({...editFormData, account_server: e.target.value})}
                className="themed-text-field"
                InputLabelProps={{ style: { fontFamily: 'Vazirmatn' } }}
              />
              <TextField
                label="وبسایت بروکر"
                fullWidth
                value={editFormData.broker_website}
                onChange={(e) => setEditFormData({...editFormData, broker_website: e.target.value})}
                className="themed-text-field"
                InputLabelProps={{ style: { fontFamily: 'Vazirmatn' } }}
              />
              <TextField
                label="شماره حساب"
                fullWidth
                value={editingAccount?.account_number || ''}
                disabled
                className="themed-text-field"
                InputLabelProps={{ style: { fontFamily: 'Vazirmatn' } }}
                helperText="شماره حساب قابل ویرایش نیست"
              />
           </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ fontFamily: 'Vazirmatn', color: 'var(--text-color)' }}>
            انصراف
          </Button>
          <Button 
            onClick={submitEditAccount} 
            color="primary" 
            variant="contained" 
            disabled={actionLoading}
            sx={{ fontFamily: 'Vazirmatn' }}
          >
            {actionLoading ? <CircularProgress size={20} color="inherit" /> : 'ذخیره تغییرات'}
          </Button>
        </DialogActions>
      </Dialog>
      <Modal open={viewSettingsOpen} onClose={handleCloseViewSettings} aria-labelledby='view-settings-title'>
        <Box
          sx={{
            maxWidth: '90%',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: theme === 'dark' ? '#222' : '#fff',
            borderRadius: '10px',
            boxShadow: 24,
            p: 4,
            color: 'var(--text-color)',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}
        >
          <ViewSettingsComponent
            loading={viewLoading}
            settings={settingsToView}
            options={optionsForView}
            planName={activePlanName}
          />
        </Box>
      </Modal>

      <Paper className='user-panel-container'>
        <Box className='user-panel-header'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <div className='logo' style={{ flexShrink: 0 }}>
              <Link to={`/?theme=${theme}`}>
                <img
                  src={theme === 'dark' ? logoImage_1 : logoImage_2}
                  alt='لوگوی نئون'
                  style={{ height: '35px', display: 'block' }}
                />
              </Link>
            </div>
            <Typography variant='h4' component='h1'>
              پنل کاربری
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {wallet !== null && (
              <div
                className={clsx(
                  'min-w-20 px-2 py-1 text-sm flex items-center justify-center rounded',
                  theme === 'dark' ? 'bg-blue-200/15 text-white' : 'bg-blue-400 text-white'
                )}
              >
                <span className='material-icons ml-1'>account_balance_wallet</span>
                <span className='mt-0.5'>${wallet / 100}</span>
              </div>
            )}
            <Button
              variant='contained'
              className='panel-btn'
              color='error'
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
            >
              خروج
            </Button>
          </Box>
        </Box>
        <Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
            allowScrollButtonsMobile
            aria-label='user panel tabs'
          >
            <Tab label='پروفایل کاربری' id='user-panel-tab-0' aria-controls='user-panel-tabpanel-0' />
            <Tab label='حساب ها' id='user-panel-tab-1' aria-controls='user-panel-tabpanel-1' />
            <Tab label='صورتحساب' id='user-panel-tab-2' aria-controls='user-panel-tabpanel-2' />
            <Tab label='دعوت دوستان' id='user-panel-tab-3' aria-controls='user-panel-tabpanel-3' />
            <Tab label='تیکت' id='user-panel-tab-4' aria-controls='user-panel-tabpanel-4' />

            {isAdmin && (
              <Tab
                label='پنل مدیریت'
                component={Link}
                to={`/admin?theme=${theme}`}
                sx={{
                  backgroundColor: 'var(--primary-color)',
                  color: '#fff !important',
                  borderRadius: '8px',
                  margin: '4px 8px',
                  '&:hover': {
                    backgroundColor: 'hsl(199, 100%, 60%)'
                  }
                }}
              />
            )}
          </Tabs>
        </Box>
        <TabPanel value={tabValue} index={0}>
          <AccountProfile />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ my: 2 }}>
            <Typography
              variant='h5'
              component='h2'
              sx={{
                mb: 2,
                color: 'var(--text-color)',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontFamily: 'Vazirmatn, sans-serif'
              }}
            >
              <HiOutlineServerStack /> حساب‌های شما
            </Typography>
            {accountsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : accountsError ? (
              <Alert severity='error'>{accountsError}</Alert>
            ) : (
              <>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {userAccounts.length > 0 ? (
                    userAccounts.map((account, index) => (
                      <Paper
                        key={account.id || `account-${index}`}
                        sx={{
                          p: 2,
                          background: 'var(--pdp-feature-card-bg)',
                          border: '1px solid var(--pdp-feature-card-border)',
                          borderRadius: '8px',
                          position: 'relative' // For absolute positioning if needed, or just normal flow
                        }}
                      >
                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Box>
                                <Typography
                                variant='body1'
                                sx={{ color: 'var(--text-color)', fontWeight: 'bold', fontFamily: 'Vazirmatn, sans-serif' }}
                                >
                                شماره حساب: {account.account_number}
                                </Typography>
                                <Typography
                                variant='body2'
                                sx={{ color: 'var(--text-muted-color)', fontFamily: 'Vazirmatn, sans-serif' }}
                                >
                                سرور: {account.account_server}
                                </Typography>
                                <Typography
                                variant='body2'
                                sx={{ color: 'var(--text-muted-color)', fontFamily: 'Vazirmatn, sans-serif' }}
                                >
                                نوع حساب: {account.account_type === 'personal' ? 'شخصی' : 'پراپ'}
                                </Typography>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant='body2'
                                    sx={{
                                      color: account.plan ? 'green' : 'orange',
                                      fontWeight: 'bold',
                                      fontFamily: 'Vazirmatn, sans-serif'
                                    }}
                                  >
                                    پلن فعال: {account.plan ? `${account.plan.title}` : 'ندارد'}
                                  </Typography>
                                  {accountsWithFullDiscount.has(account.id) && (
                                    <Chip label='دمو' color='info' size='small' sx={{ ml: 1 }} />
                                  )}
                                </Box>

                                <Typography
                                variant='body2'
                                sx={{
                                    color: account.is_active ? 'green' : 'red',
                                    fontWeight: 'bold',
                                    fontFamily: 'Vazirmatn, sans-serif',
                                    mt: 1
                                }}
                                >
                                وضعیت حساب: {account.is_active ? 'فعال' : 'غیرفعال'}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="ویرایش اطلاعات حساب">
                                    <IconButton 
                                        onClick={() => handleEditAccountClick(account)} 
                                        sx={{ color: 'var(--link-color)' }}
                                        size="small"
                                    >
                                        <HiPencil />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="حذف حساب">
                                    <IconButton 
                                        onClick={() => handleDeleteAccountClick(account.id)} 
                                        color="error"
                                        size="small"
                                    >
                                        <HiTrash />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          {account.plan ? (
                            <>
                              <Button
                                variant='contained'
                                sx={{
                                  background: 'linear-gradient(to right, hsl(199, 100%, 50%), hsl(324, 100%, 50%))',
                                  color: 'white',
                                  fontFamily: 'Vazirmatn, sans-serif'
                                }}
                                onClick={() => handleConfigurePlanClick(account)}
                                startIcon={<HiPencilSquare style={{ marginLeft: '5px' }} />}
                              >
                                تغییر تنظیمات پلن
                              </Button>
                              <Button
                                variant='outlined'
                                sx={{
                                  color: 'var(--primary-color)',
                                  borderColor: 'var(--primary-color)',
                                  fontFamily: 'Vazirmatn, sans-serif',
                                  '&:hover': {
                                    borderColor: 'var(--primary-color)',
                                    background: 'rgba(0, 180, 216, 0.1)'
                                  }
                                }}
                                onClick={() => handleViewSettingsClick(account)}
                                startIcon={<HiEye style={{ marginLeft: '5px' }} />}
                              >
                                مشاهده تنظیمات پلن
                              </Button>
                            </>
                          ) : (
                            <Button
                              component={Link}
                              to={`/plans?theme=${theme}`}
                              variant='contained'
                              sx={{
                                background: 'linear-gradient(to right, #28a745, #218838)',
                                color: 'white',
                                fontFamily: 'Vazirmatn, sans-serif',
                                '&:hover': {
                                  background: 'linear-gradient(to right, #218838, #1e7e34)'
                                }
                              }}
                              startIcon={<HiOutlineShoppingBag style={{ marginLeft: '5px' }} />}
                            >
                              خرید پلن
                            </Button>
                          )}
                        </Box>
                      </Paper>
                    ))
                  ) : (
                    <Typography
                      sx={{
                        color: 'var(--text-muted-color)',
                        textAlign: 'center',
                        fontFamily: 'Vazirmatn, sans-serif'
                      }}
                    >
                      هیچ حسابی یافت نشد.
                    </Typography>
                  )}
                </Box>
                <Button
                  variant='contained'
                  sx={{
                    mt: 3,
                    background: 'var(--pdp-backlink-bg)',
                    color: 'var(--pdp-select-btn-text)',
                    fontFamily: 'Vazirmatn, sans-serif'
                  }}
                  onClick={handleAddAccountClick}
                >
                  افزودن حساب جدید
                </Button>
              </>
            )}
          </Box>
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <PurchaseHistory />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <Reffralcode />
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          <RenderTicketTab />
        </TabPanel>
      </Paper>
    </Container>
  )
}

export default UserPanel
